// src/models/Insights/InsightsTypes.ts
//
// Domain types for the Navigator Insights tab. Every ratio/count that Cube can return as
// null (Rule 1) is typed `number | null`, never `number` - callers must handle the null
// case explicitly rather than accidentally rendering a coerced `0`.

export type FleetStatus = {
  totalReportingDevices: number | null;
  recentlyActive: number | null;
  reportedTodayUtc: number | null;
  offlineOver24h: number | null;
  lowBattery: number | null;
};

export type SafetySummary = {
  complianceRatio: number | null; // protected / valid, whole window
  muteRatio: number | null; // muted / valid, whole window
  reportingCoverageRatio: number | null;
};

export type CompliancePoint = {
  date: string; // YYYY-MM-DD, UTC
  complianceRatio: number | null;
  muteRatio: number | null;
};

/**
 * `getComplianceTrend`'s full result: the day-grain points for the chart, plus the
 * whole-window headline ratios recomputed as sum(protected)/sum(valid) and
 * sum(muted)/sum(valid) across those same rows (Task 2.3) - this is deliberately not the
 * mean of the daily ratios, which would misweight low-valid-seconds days.
 */
export type ComplianceTrendResult = {
  points: CompliancePoint[];
  headlineComplianceRatio: number | null;
  headlineMuteRatio: number | null;
};

/**
 * Whole-window per-feature safety compliance scores (arc, fall, head impact). Each score is
 * `1 - fail_ratio` for its feature, computed from the non-additive `DailyOrgRollup`
 * `*_fail_ratio` diagnostics (see `getFeatureCompliance`) - never sum these three fields
 * together, they are independent per-feature diagnostics, not a partition of one "total"
 * compliance number. `null` propagates from a null fail ratio (zero applicable seconds in
 * the window) and must never be coerced to `0` (Rule 1). `headScore` is `null` both when the
 * window has no data AND when the org is Core-only (head-impact detection is Pro-only, so
 * `head_applicable_seconds` sums to 0 for a Core fleet) - the UI distinguishes this case with
 * a dedicated "not applicable" caption rather than the generic "not enough data" one.
 */
export type FeatureComplianceSummary = {
  arcScore: number | null; // 1 - arc_fail_ratio, whole window
  fallScore: number | null; // 1 - fall_fail_ratio, whole window
  headScore: number | null; // 1 - head_fail_ratio, whole window; null also means "not applicable" for Core-only orgs
};

/**
 * Whole-window device reporting hours, sourced from `DailyDeviceRollup` (device-minute
 * grain, NOT `DailyOrgRollup`'s org-minute grain - see `getProtectedHours`'s header
 * comment for why). `validHours` is the denominator ("of N device reporting hours");
 * `protectedHours` is the numerator. Both are raw hour counts, not a ratio - the Protected
 * Hours card renders `protectedHours` as its headline value with `validHours` folded into
 * the caption, rather than a percent (see `UtilizationSection`'s header comment for why a
 * count reads more honestly here than a ratio would). Labeling constraint: always "device
 * reporting hours" in any UI copy that surfaces these fields - never "work hours" or
 * "logged/scheduled field hours" (this measures Compass-powered-on-and-paired time, not
 * scheduled shift time).
 */
export type ProtectedHoursSummary = {
  protectedHours: number | null;
  validHours: number | null;
};

/**
 * `devicesReportingInWindow` is the distinct-device numerator for the selected date range,
 * sourced from `DailyDeviceRollup` (see `getUtilization`'s header comment for the
 * de-duplication rule). Its denominator (`DeviceLatestStatus.device_count`, all-time
 * cumulative) is NOT part of this type - it is already available via `FleetStatus.
 * totalReportingDevices`, and `UtilizationSection` takes that number as a prop rather than
 * this DAO issuing a second redundant query for it. `noTelemetryIn14Days` is a separate,
 * independent stale-device count (also from `DeviceLatestStatus`), never a partition or
 * complement of `devicesReportingInWindow` - a device can be in neither, either, or both
 * counts depending on window length and its actual last-report time.
 */
export type UtilizationSummary = {
  devicesReportingInWindow: number | null;
  noTelemetryIn14Days: number | null;
};

/**
 * Whole-window distinct Compass Core vs. Compass Pro device counts for the "Core vs. Pro
 * Devices" comparison card (`getCoreProDeviceCounts`, InsightsDAO.ts) - how many distinct
 * devices of each class reported at least once in the selected window, NOT a ratio of
 * protected/valid time (that was this card's previous, now-replaced metric).
 *
 * Deliberately plain `number`, never `number | null` (contrast with the ratio types
 * elsewhere in this file): a count of zero devices for one class is real, meaningful data
 * (an org that is 100% Pro genuinely has `coreDeviceCount: 0`), not a "not enough data"
 * signal - Rule 1's null-for-undefined-division guard does not apply to a plain count with
 * no denominator to divide by. `getCoreProDeviceCounts`'s own `Set`-based reduction already
 * defaults an empty/no-rows-matched class to `0` rather than leaving it `undefined`, so this
 * type's non-nullability is a real contract, not just an assertion.
 *
 * Decision on unknown/undetermined device class (see `getCoreProDeviceCounts`'s own header
 * comment for the full evidence): a device whose `is_core` is null for every row it
 * contributed in the window is excluded from BOTH counts, the same way it is already
 * excluded from both `core_compliance_ratio`'s and `pro_compliance_ratio`'s own
 * numerator/denominator (see `DailyDeviceRollup.js`) - there is no third "unknown class"
 * bucket on this two-sided card. This is a real, if now historically-limited, possibility
 * (confirmed via `docs/guides/2026-09-02-mute-ratio-unknown-class-issue.md`'s per-org
 * `is_core IS NULL` prevalence table - up to 100% of events for some dev/test orgs on
 * legacy `schema_ver = "1"` payloads that omit `is_core` entirely; every current app build
 * always sends it), so `coreDeviceCount + proDeviceCount` is NOT guaranteed to equal the
 * total distinct devices reporting in the window - a device seen only with unknown class
 * contributes to neither side. This is called out here, not hidden, precisely because it is
 * a real possibility, not a theoretical one.
 */
export type CoreProDeviceCountSummary = {
  coreDeviceCount: number;
  proDeviceCount: number;
};

export type DeviceStatusSlice = { label: string; count: number };
export type VersionBucket = { version: string; group: string; count: number };

export type VersionDistribution = {
  firmware: VersionBucket[];
  mobile: VersionBucket[];
};

/**
 * One `DeviceLatestStatus` row for the Firmware Version Distribution card's "View details"
 * dialog (`getFirmwareDeviceDetails`) - row-level, unlike `VersionBucket` which is already
 * grouped/counted. `macAddress` (`c_mac`) is the dialog's displayed device identifier -
 * chosen over `c_hw_id` after a live Cube investigation confirmed `c_hw_id` is essentially
 * unpopulated for devices stuck at the bootloader sentinel (0 of 10 sampled rows; 65%
 * fleet-wide) while `c_mac` is populated in 9 of 10 sampled rows (97% fleet-wide) - using
 * `c_hw_id` made every "Stuck in bootloader"/"No reported firmware" row unidentifiable,
 * exactly where identifying a device matters most. `firmwareVersion` is already normalized
 * to `UNKNOWN_VERSION_LABEL` for a null/empty raw `c_fw`, the same mapping `versionBucket`
 * (InsightsDAO.ts) applies, so callers can classify a row with a plain
 * `=== UNKNOWN_VERSION_LABEL` check instead of re-deriving that sentinel themselves.
 *
 * `isCompassCore` (`is_core`) was added so a MAC clicked from the "View details" dialog can
 * open the same device-card overlay (`UserDashboard`) the Device Management tab opens for a
 * device row - the overlay's `CompassInfoSnapshot.is_compass_core` is a non-nullable
 * `boolean`, but the underlying Cube dimension can be null/undetermined (no seed data, a
 * legacy row - see `isCoreLabel`'s own doc comment), so this field is typed `boolean | null`
 * rather than defaulted to `false` here - the caller building the device-card override
 * decides how to collapse the null case (see `FirmwareDetailsDialog.buildFirmwareDeviceOverride`),
 * this type just carries the genuine three-state fact through undisturbed (Rule 1).
 */
export type FirmwareDeviceRow = {
  deviceKey: string;
  macAddress: string;
  firmwareVersion: string;
  isCompassCore: boolean | null;
};

/** Bucket label for null/empty string dimensions (c_fw, app_ver, phone_type) - never drop the row. */
export const UNKNOWN_VERSION_LABEL = 'Unknown';

/**
 * The three canonical labels for the donut's exclusive is_recently_active/is_offline_over_24h
 * partition (Rule 5). Defined once here so `InsightsDAO.deviceStatusSliceLabel` (the only
 * place that maps the raw Cube dimensions to a label) and `DeviceStatusDonut` (which
 * recognizes canonical labels vs. anomalies) share a single source of truth instead of two
 * independently-maintained copies of the same three strings.
 */
export const RECENTLY_ACTIVE_SLICE_LABEL = 'Recently active (last hour)';
export const SEEN_LAST_24H_SLICE_LABEL = 'Seen in the last 24 hours';
export const NOT_SEEN_OVER_24H_SLICE_LABEL = 'Not seen in over 24 hours';

export const EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS = [
  RECENTLY_ACTIVE_SLICE_LABEL,
  SEEN_LAST_24H_SLICE_LABEL,
  NOT_SEEN_OVER_24H_SLICE_LABEL,
] as const;

export type WorkZoneBreachChannel = 'voltage' | 'current';

/**
 * Position at the LAST sample of a Work Zone Breach (design spec §4.4). Absent when that
 * sample carried no GPS fix - location sharing is user-controlled, so this is a normal
 * outcome, not an error. Never construct this object with zeroes to represent absence:
 * the whole value is null, or it is a real coordinate (`0` is a legal latitude AND a legal
 * longitude, so it cannot be used as a sentinel here or anywhere downstream of it).
 */
export type WorkZoneBreachLocation = {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
};

/**
 * One Work Zone Breach: a sustained maximum-alert run in Work Zone Adapt (PRD D8). One row
 * per run, per channel - voltage and current are never merged (design spec §6, PRD R5).
 * `durationSeconds` is measured on the device's REPORTED alert level, which the firmware
 * debounces, so it understates how long the underlying field condition persisted. Never
 * present it as the duration of a physical condition (PRD D4).
 *
 * `location` is a nested nullable object, not three sibling nullable fields (design §11.1),
 * so "no location" is one unambiguous state (`location === null`) rather than a combination
 * the renderer has to reason about - a half-coordinate is not representable through this type.
 *
 * No `muteState` (PRD D7 - mute is excluded from v1 entirely). No device-class field: the
 * mart records the observed class as an operations/diagnostic signal (design §4.7), but this
 * UI-facing type deliberately omits it - exposing it here would invite a UI-side class
 * filter, which is exactly the sample-level gate M2 removed from the pipeline; adding it
 * back one layer up in the UI type would defeat that removal.
 *
 * `authId` / `distinctAuthIdCount` (M3, user attribution feature; design spec §5.1) are the
 * Cube-shaped passthrough of the mart's two auth_id-derived columns - deliberately NO
 * `displayName` and NO `groupName` on this type. Resolving `authId` to a name/group is a
 * SEPARATE layer (a later milestone's resolver, built over `OrganizationProvider`'s roster)
 * that this type knows nothing about; this type is the Cube boundary only.
 */
export type WorkZoneBreach = {
  id: string; // org:channel:mac:startTs - stable React key
  channel: WorkZoneBreachChannel;
  macAddress: string; // authoritative device identity
  deviceKey: string; // shipped derivation; display/join only, may collide
  startTsUtc: string; // ISO 8601, UTC
  endTsUtc: string;
  durationSeconds: number;
  sampleCount: number;
  activeSetting: number | null;
  location: WorkZoneBreachLocation | null; // null = no fix reported. NEVER {0, 0}.
  firmwareVersion: string;
  /**
   * Opaque Auth0 subject id for the wearer at the run's last sample. NEVER rendered directly
   * (a later milestone's PRD forbids showing the raw id to a customer) - it is meaningless to
   * a customer and it is a person-linked identifier.
   *
   * `''` means "no user recorded on this row" - pre-promote history, or a sample that carried
   * no identity. This is DISTINCT from "auth_id present but unresolvable against the roster"
   * (a user who left the org, is in no group, or belongs to a different org) - collapsing the
   * two states here would make it impossible to tell "history has not been swept" from "the
   * join is broken" downstream, which is exactly the ambiguity a later milestone's two
   * separate fallback strings (PRD OQ1) exist to keep apart. A future resolver layer needs
   * this distinction preserved at the type; do not coerce `''` to `null` or treat it as
   * absent anywhere between the Cube boundary and that resolver.
   */
  authId: string;
  /**
   * Distinct wearers observed across the run. Expected 1; >1 is a shared-device run whose row
   * carries only the last wearer (a device-pairing anomaly, not a filter or a segment).
   * Carried through deliberately unrendered in v1 - same treatment `truncated` and
   * `durationSeconds` get elsewhere on this card.
   */
  distinctAuthIdCount: number;
};

// NOTE ON THE MISSING AUTOMATED TEST FOR "no displayName/groupName on WorkZoneBreach":
// TypeScript structural types have no runtime footprint, so there is no `keyof` list or
// object shape to assert against at test time - `WorkZoneBreach` erases entirely at compile
// time, and a test importing only the *type* (not a value) cannot inspect its declared
// fields. The absence is enforced at compile time instead: a resolver or component that spreads
// `displayName`/`groupName` onto a `WorkZoneBreach`-typed object, or that reads either field
// off one, fails `tsc` with "Property 'displayName' does not exist on type 'WorkZoneBreach'" -
// which is a stronger, earlier guarantee than a runtime assertion would be, not a weaker one.
// This comment stands in place of the automated test the plan calls for, per its own
// instruction to say so plainly rather than inventing a framework for one field.

/**
 * `getWorkZoneBreaches`'s result (InsightsDAO.ts) - a dimensions-only, row-level query is
 * unbounded by construction, so the DAO applies an explicit row limit and surfaces
 * `truncated` here rather than silently dropping rows past the limit with no signal to the
 * caller (design §11.2).
 */
export type WorkZoneBreachQueryResult = {
  breaches: WorkZoneBreach[];
  truncated: boolean;
};
